import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

interface RoomMotionCardProps {
  room: { label: string; href: string; img: string };
  fallbackImage: string;
  index: number;
}

export function RoomMotionCard({ room, fallbackImage, index }: RoomMotionCardProps) {
  const ref = useRef<HTMLAnchorElement>(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || !window.IntersectionObserver) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setEntered(true);
      observer.disconnect();
    }, { threshold: 0.15 });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Link
      ref={ref}
      to={room.href}
      aria-label={room.label}
      className={`room-motion-card group relative block overflow-hidden shrink-0 w-[68%] sm:w-[44%] md:w-auto snap-start ${entered ? "room-motion-entered" : ""}`}
      style={{ "--room-motion-delay": `${Math.min(index * 65, 260)}ms` } as React.CSSProperties}
    >
      <img
        src={room.img}
        alt={room.label}
        className="room-motion-image w-full h-[220px] md:h-[300px] object-cover"
        loading="lazy"
        onError={(event) => {
          if (event.currentTarget.getAttribute("src") !== fallbackImage) event.currentTarget.src = fallbackImage;
        }}
      />
      <div className="room-motion-overlay absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/10 to-transparent" />
      <div className="room-motion-label absolute bottom-3 left-3 right-2 flex items-center justify-between gap-2 text-primary-foreground">
        <span className="text-[14px] md:text-[13px] font-bold">{room.label}</span>
        <span aria-hidden="true" className="room-motion-arrow h-8 w-8 shrink-0 rounded-full bg-background/95 text-foreground flex items-center justify-center">
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}